import type {
  InsertAssessmentInput,
  InsertInternalAuditReportMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  GetInternalAuditReportByIdDocument,
  GetInternalAuditReportsDocument,
  InsertInternalAuditReportDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useInsertInternalAuditReport = createMutationHook({
  name: 'insertInternalAuditReport',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.internalAuditReport.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  // The report register is a near-1:1 twin of assessment and shares the
  // InsertAssessmentInput wire shape, so the Status field is the assessment
  // status enum (the DB stores an InternalAuditStatus with identical values).
  mapInput: (variables: InsertAssessmentInput) => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
  }),
  invalidate: (trpc) => [
    trpc.frontend.internalAuditReport.register.queryKey(),
    trpc.frontend.internalAuditReport.internalAuditReportCount.queryKey(),
    trpc.frontend.internalAuditReport.internalAuditReportsByOriginatingItemId.queryKey(),
    // internal_audit_report and internal_audit_entity are the same table;
    // mirror the Apollo eviction of `internal_audit_entity` so the entity
    // register / by-id caches refresh too.
    trpc.frontend.internalAuditEntity.register.queryKey(),
    trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertInternalAuditReportMutation => ({
    insertInternalAuditReportApi: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertInternalAuditReportDocument,
  graphqlVariables: (variables: InsertAssessmentInput) => ({
    object: variables,
  }),
  evictFields: [
    'internal_audit_entity',
    'internal_audit_report',
    'internal_audit_report_aggregate',
  ],
  refetchQueries: [
    GetInternalAuditReportByIdDocument,
    GetInternalAuditReportsDocument,
  ],
});
